<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Relatório de Contas a Pagar</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#financeiro')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <div class="d-flex flex-wrap align-center mt-4 mb-4" style="gap: 12px;">
      <v-text-field v-model="filtros.dataInicio" type="date" label="Vencimento inicial" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.dataFim" type="date" label="Vencimento final" solo hide-details clearable class="c-filtro" />

      <v-autocomplete v-model="filtros.empresa" :items="empresaOptions" label="Empresa" clearable solo hide-details class="c-filtro" />
      <v-autocomplete v-model="filtros.fornecedor" :items="fornecedorOptions" label="Fornecedor" clearable solo hide-details class="c-filtro" />

      <v-btn color="primary" :loading="loading" @click="buscarDados">
        <v-icon left>mdi-refresh</v-icon>
        Recarregar
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" outlined v-on="on" :disabled="loading || dadosFiltrados.length === 0">
            <v-icon left>mdi-file-export-outline</v-icon>
            Exportar
          </v-btn>
        </template>
        <v-list>
          <v-list-item link @click="gerarExcel"><v-list-item-title>Excel</v-list-item-title></v-list-item>
          <v-list-item link @click="gerarPdf"><v-list-item-title>PDF</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div ref="areaPdf">
      <v-data-table :headers="headers" :items="dadosFiltrados" :loading="loading" item-key="_rowKey" rows-per-page-text="Linhas por página:" page-text="{0}-{1} de {2}" class="elevation-1">
        <template v-slot:item.dataEmissao="{ item }">{{ formatarDataPtBr(item.dataEmissao) }}</template>
        <template v-slot:item.vencimento="{ item }">{{ formatarDataPtBr(item.vencimento) }}</template>
        <template v-slot:item.valorParcela="{ item }">{{ formatarMoedaPtBr(item.valorParcela) }}</template>
        <template v-slot:item.valorTotalNota="{ item }">{{ formatarMoedaPtBr(item.valorTotalNota) }}</template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import relatorioContasAPagarService from "@/views/Relatorios/services/relatorioContasAPagarService";
import { exportarExcel, exportarPdfTabela, formatarDataPtBr, formatarMoedaPtBr, normalizarTexto } from "@/views/Relatorios/base/relatorioBaseUtils";

export default {
  name: "RelatorioContasAPagarView",
  data() {
    return {
      loading: false,
      dados: [],
      filtros: { dataInicio: "", dataFim: "", empresa: "", fornecedor: "" },
      headers: [
        { text: "Empresa", value: "empresa" },
        { text: "Pedido", value: "pedido" },
        { text: "Ordem de Pagamento", value: "ordemPagamento" },
        { text: "JOB", value: "job" },
        { text: "OS", value: "os" },
        { text: "Centro de Custo", value: "centroCusto" },
        { text: "GrupoDesp", value: "grupoDesp" },
        { text: "Número NF", value: "numeroNotaFiscal" },
        { text: "Data Emissão", value: "dataEmissao" },
        { text: "Vencimento", value: "vencimento" },
        { text: "Num. Parcelas", value: "numParcelas" },
        { text: "Valor Parcela", value: "valorParcela" },
        { text: "Valor Total Nota", value: "valorTotalNota" },
        { text: "Remetente", value: "remetente" },
        { text: "CNPJ Emitente", value: "cnpjEmitente" },
        { text: "CFOP", value: "cfop" },
        { text: "Natureza da Operação", value: "naturezaOperacao" },
        { text: "Informações Complementares", value: "infoComplementares" }
      ]
    };
  },
  computed: {
    empresaOptions() {
      return Array.from(new Set(this.dados.map((item) => normalizarTexto(item.empresa)).filter(Boolean))).sort();
    },
    fornecedorOptions() {
      return Array.from(new Set(this.dados.map((item) => normalizarTexto(item.remetente)).filter(Boolean))).sort();
    },
    dadosFiltrados() {
      const inicio = this.filtros.dataInicio ? new Date(this.filtros.dataInicio) : null;
      const fim = this.filtros.dataFim ? new Date(this.filtros.dataFim) : null;
      if (fim) fim.setHours(23, 59, 59, 999);

      const filtrados = this.dados.filter((item) => {
        const dataLinha = item.vencimento ? new Date(item.vencimento) : null;
        const empresaOk = !this.filtros.empresa || normalizarTexto(item.empresa) === normalizarTexto(this.filtros.empresa);
        const fornecedorOk = !this.filtros.fornecedor || normalizarTexto(item.remetente) === normalizarTexto(this.filtros.fornecedor);

        if (!dataLinha || Number.isNaN(dataLinha.getTime())) return empresaOk && fornecedorOk;

        const dataInicioOk = !inicio || dataLinha >= inicio;
        const dataFimOk = !fim || dataLinha <= fim;
        return empresaOk && fornecedorOk && dataInicioOk && dataFimOk;
      });

      return filtrados.map((item, index) => ({
        ...item,
        _rowKey: `${normalizarTexto(item.numeroNotaFiscal)}-${normalizarTexto(item.pedido)}-${normalizarTexto(item.remetente)}-${index}`
      }));
    }
  },
  watch: {
    'filtros.dataInicio'() { this.buscarDados(); },
    'filtros.dataFim'() { this.buscarDados(); }
  },
  methods: {
    formatarDataPtBr,
    formatarMoedaPtBr,
    async buscarDados() {
      this.loading = true;
      try {
        const lista = await relatorioContasAPagarService.listarContasAPagar(this.filtros);
        this.dados = Array.isArray(lista) ? lista : [];
      } finally {
        this.loading = false;
      }
    },
    gerarExcel() {
      exportarExcel(this.dadosFiltrados, "relatorio-contas-a-pagar.xlsx");
    },
    async gerarPdf() {
      await exportarPdfTabela({
        headers: this.headers,
        rows: this.dadosFiltrados,
        nomeArquivo: "relatorio-contas-a-pagar.pdf",
        tituloRelatorio: "Relatório de Contas a Pagar",
        formatadoresPorCampo: {
          dataEmissao: (valor) => this.formatarDataPtBr(valor),
          vencimento: (valor) => this.formatarDataPtBr(valor),
          valorParcela: (valor) => this.formatarMoedaPtBr(valor),
          valorTotalNota: (valor) => this.formatarMoedaPtBr(valor)
        }
      });
    }
  },
  async mounted() {
    await this.buscarDados();
  }
};
</script>

<style scoped>
.c-filtro { min-width: 220px; max-width: 280px; }
</style>
